import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

import Prompt from "lib/components/prompt/Prompt";

import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";
import FeedbackIconButton from "utils/FeedbackIconButton";
import { asTitle } from "utils/Utils";
import { Event } from "utils/history/Event";
import History from "utils/history/History";

import { getLoggedInUser } from "authentication/AuthenticationService";

import { Field } from "core/field/FieldService";
import UserAccessOverview from "core/trajectory/access/UserAccessOverview";

import { Report } from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { canUserModifyRights } from "./AccessUtil";
import { TrajectoryAccess, updateTrajectoryAccess } from "./TrajectoryService";

export type AccessViewProps = {
	trajectoryId: number;
	access: TrajectoryAccess;
	information: Field[];
	reports: Report[];
	onSaveAccess: (access: TrajectoryAccess) => void;
};

function AccessView(props: AccessViewProps): JSX.Element {
	const { t } = useTranslation();

	const [access, setAccess] = useState(props.access);
	const [history, setHistory] = useState(
		new History<TrajectoryAccess>(access)
	);

	const [edit, setEdit] = useState(false);

	const [successMessage, setSuccessMessage] = useState<string>();
	const [errorMessage, setErrorMessage] = useState<string>();

	useEffect(() => {
		setAccess(props.access);
		setHistory(new History<TrajectoryAccess>(props.access));
	}, [props.access]);

	const updateAccess = useCallback(
		(event: Event): void => {
			setAccess(history.apply(event.toAction()));
		},
		[history]
	);

	const saveAccess = useCallback((): Promise<void> => {
		const actions = history.getActions();
		if (actions.length === 0) {
			return Promise.resolve();
		}

		const { roles, users } = access;
		if (!canUserModifyRights(getLoggedInUser(), roles, users)) {
			setErrorMessage(t("canNotRevokeRight"));
			return Promise.resolve();
		}

		return updateTrajectoryAccess(props.trajectoryId, access)
			.then(() => {
				setEdit(false);
				setHistory(new History<TrajectoryAccess>(access));
			})
			.then(() => props.onSaveAccess(access))
			.then(() => {
				setSuccessMessage(
					t("saveDocumentSuccess", {
						document: "right_other"
					})
				);
				setErrorMessage(undefined);
			})
			.catch((reason: Error) => {
				let errorMessage: string;
				if (reason.message === ERR_INTERNET_DISCONNECTED) {
					errorMessage = t("internetDisconnectedError");
				} else {
					errorMessage = t("saveDocumentFailure", {
						document: "form"
					});
				}

				setSuccessMessage(undefined);
				setErrorMessage(errorMessage);
			});
	}, [props.trajectoryId, props.onSaveAccess, access, history]);

	const closeAccess = useCallback((): void => {
		if (
			!history.hasPrevious() ||
			window.confirm(t("exitDocumentMessage"))
		) {
			setEdit(false);
			setAccess(props.access);
			setHistory(new History<TrajectoryAccess>(props.access));
		}
	}, [history]);

	return (
		<>
			{errorMessage && (
				<Alert
					id="error-message"
					severity="error"
					onClose={(): void => setErrorMessage(undefined)}>
					{errorMessage}
				</Alert>
			)}
			{successMessage && (
				<Alert
					id="success-message"
					severity="success"
					onClose={(): void => setSuccessMessage(undefined)}>
					{successMessage}
				</Alert>
			)}

			{!edit && (
				<Button
					disabled={
						!canUserModifyRights(
							getLoggedInUser(),
							access.roles,
							access.users
						)
					}
					variant="outlined"
					onClick={() => setEdit(true)}
					startIcon={<EditIcon />}>
					{asTitle(t("editAccess"))}
				</Button>
			)}
			{edit && (
				<Grid container>
					<Grid item>
						<Tooltip title={asTitle(t("save"))}>
							<span>
								<FeedbackIconButton
									id="save-access-button"
									aria-label="save-access"
									disabled={!history.hasPrevious()}
									onClick={saveAccess}>
									<SaveIcon />
								</FeedbackIconButton>
							</span>
						</Tooltip>
					</Grid>
					<Grid item>
						<Tooltip title={asTitle(t("cancel"))}>
							<span>
								<IconButton
									id="close-access-button"
									aria-label="close-access"
									onClick={closeAccess}>
									<CloseIcon />
								</IconButton>
							</span>
						</Tooltip>
					</Grid>
				</Grid>
			)}

			<UserAccessOverview
				roles={access.roles}
				users={access.users}
				information={props.information}
				reports={props.reports}
				editable={edit}
				onChange={updateAccess}
			/>

			{edit && (
				<Prompt
					when={history.hasPrevious()}
					message={t("exitDocumentMessage")}
				/>
			)}
		</>
	);
}

export default AccessView;
